'use client';

import { useCallback, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { useOnboardingState } from '@/src/lib/onboarding/use-onboarding-state';
import { useWorkflowState } from '@/src/lib/onboarding/use-workflow-state';
import { findTour, toursForContext, type Tour } from '@/src/lib/onboarding/tours';
import { workflowsForContext, findWorkflow } from '@/src/lib/onboarding/workflows';
import {
  NAVIGATION_CONTEXTS,
  type NavigationContext,
} from '@/src/lib/navigation';
import { ErrorBoundary } from '@/src/components/ui-kontora/error-boundary';

const WelcomeModal = dynamic(
  () => import('./welcome-modal').then((m) => ({ default: m.WelcomeModal })),
  { ssr: false },
);

const TourOverlay = dynamic(
  () => import('./tour-overlay').then((m) => ({ default: m.TourOverlay })),
  { ssr: false },
);

export interface TourLauncherProps {
  availableContexts: readonly NavigationContext[];
  activeContext: NavigationContext;
  userDisplayName: string;
  userPermissions?: readonly string[];
}

export function TourLauncher({
  availableContexts,
  activeContext,
  userDisplayName,
  userPermissions = [],
}: TourLauncherProps) {
  const {
    state,
    isReady,
    isFirstVisit,
    shouldShowTours,
    dismissWelcome,
    resetWelcome,
    completeTour,
    skipTour,
    setExperienced,
    reset,
  } = useOnboardingState();

  const { startWorkflow } = useWorkflowState(null);
  const contextWorkflows = workflowsForContext(activeContext, userPermissions);

  const pathname = usePathname();
  const router = useRouter();
  const [activeTour, setActiveTour] = useState<Tour | null>(null);

  // Welcome-Modal: leitet sich direkt aus dem State ab, nicht aus einem Effect.
  // So vermeiden wir cascading renders und Hydration-Mismatches.
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeAutoOpened, setWelcomeAutoOpened] = useState(false);
  if (isReady && isFirstVisit && !welcomeAutoOpened) {
    setWelcomeOpen(true);
    setWelcomeAutoOpened(true);
  }

  // Touren, die für den aktuellen Kontext verfügbar sind
  const contextTours = toursForContext(activeContext);

  const startTour = useCallback(
    (tourId: string) => {
      const tour = findTour(tourId);
      if (!tour) return;
      setActiveTour(tour);
    },
    [],
  );

  const startContextTour = useCallback(() => {
    const first = contextTours[0];
    if (first) startTour(first.id);
  }, [contextTours, startTour]);

  const handleComplete = useCallback(() => {
    if (activeTour) {
      completeTour(activeTour.id);
    }
    setActiveTour(null);
  }, [activeTour, completeTour]);

  const handleSkip = useCallback(() => {
    if (activeTour) {
      skipTour(activeTour.id);
    }
    setActiveTour(null);
  }, [activeTour, skipTour]);

  const handleExperienced = useCallback(() => {
    setExperienced(true);
    setWelcomeOpen(false);
  }, [setExperienced]);

  const handleShowWelcome = useCallback(() => {
    setWelcomeOpen(true);
  }, []);

  const handleShowWelcomeAgain = useCallback(() => {
    resetWelcome();
    setWelcomeOpen(true);
  }, [resetWelcome]);

  // Tastatur: Shift+? öffnet Tour-Liste (Quick-Tip)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '?' && e.shiftKey) {
        e.preventDefault();
        handleShowWelcome();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleShowWelcome]);

  // Wenn activeContext oder Pathname wechselt, offene Tour schließen.
// Context wird im Render synchronisiert; Pathname muss im Effect überwacht werden,
// weil usePathname erst nach dem Routing-Update aktualisiert wird.
  const [tourPath, setTourPath] = useState(pathname);
  if (tourPath !== pathname) {
    setTourPath(pathname);
    setActiveTour(null);
  }

  if (!isReady) return null;
  if (availableContexts.length === 0) return null;

  return (
    <>
      <TourTriggerButton
        shouldShowTours={shouldShowTours}
        hasContextTours={contextTours.length > 0}
        hasContextWorkflows={contextWorkflows.length > 0}
        onShowWelcome={handleShowWelcome}
        onShowWelcomeAgain={handleShowWelcomeAgain}
        onStartContextTour={startContextTour}
        onResetTour={() => {
          reset();
          setWelcomeOpen(true);
        }}
        activeContextMeta={NAVIGATION_CONTEXTS[activeContext]}
        contextWorkflows={contextWorkflows}
        onStartWorkflow={(code) => {
          startWorkflow(code);
          const wf = findWorkflow(code);
          if (wf) {
            setActiveTour(null);
            setWelcomeOpen(false);
            const target = wf.steps[0]?.targetPath;
            if (target) {
              router.push(target);
            }
          }
        }}
      />

      <ErrorBoundary context="TourLauncher">
        <WelcomeModal
          isOpen={welcomeOpen}
          state={state}
          availableContexts={availableContexts}
          userDisplayName={userDisplayName}
          onStartTour={startTour}
          onMarkExperienced={handleExperienced}
          onDismiss={() => {
            dismissWelcome();
            setWelcomeOpen(false);
          }}
        />

        {activeTour ? (
          <TourOverlay
            tour={activeTour}
            onComplete={handleComplete}
            onSkip={handleSkip}
          />
        ) : null}
      </ErrorBoundary>
    </>
  );
}

interface TourTriggerButtonProps {
  shouldShowTours: boolean;
  hasContextTours: boolean;
  hasContextWorkflows: boolean;
  contextWorkflows: ReadonlyArray<{ code: string; title: string; description: string }>;
  onShowWelcome: () => void;
  onShowWelcomeAgain: () => void;
  onStartContextTour: () => void;
  onResetTour: () => void;
  onStartWorkflow: (code: string) => void;
  activeContextMeta: { label: string; shortLabel: string; icon: string };
}

function TourTriggerButton({
  shouldShowTours,
  hasContextTours,
  hasContextWorkflows,
  contextWorkflows,
  onShowWelcome,
  onShowWelcomeAgain,
  onStartContextTour,
  onResetTour,
  onStartWorkflow,
  activeContextMeta,
}: TourTriggerButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <details
      className="group relative"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary
        data-tour="tour-trigger"
        aria-label="Hilfe und Touren"
        className="flex h-10 cursor-pointer list-none items-center gap-2 rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-2.5 text-[var(--k-color-text-secondary)] shadow-[var(--k-shadow-xs)] transition hover:border-[var(--k-color-border-default)] hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
      >
        <span aria-hidden="true" className="text-base font-bold">
          ?
        </span>
        {shouldShowTours ? (
          <span className="hidden text-[11px] font-bold uppercase tracking-wide text-[var(--k-color-text-link)] md:inline">
            Hilfe
          </span>
        ) : null}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="hidden text-[var(--k-color-text-muted)] transition group-open:rotate-180 md:block"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </summary>
      <div className="absolute right-0 z-40 mt-2 w-72 overflow-hidden rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] shadow-[var(--k-shadow-lg)]">
        <div className="border-b border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] p-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--k-color-text-muted)]">
            Hilfe & Touren
          </p>
          <p className="mt-1 text-xs text-[var(--k-color-text-secondary)]">
            Du bist im Kontext <span className="font-semibold">{activeContextMeta.label}</span>.
          </p>
        </div>
        <div className="p-2">
          {hasContextTours && shouldShowTours ? (
            <button
              type="button"
              onClick={() => {
                onStartContextTour();
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-[var(--k-radius-sm)] px-3 py-2 text-sm font-medium text-[var(--k-color-text-link)] transition hover:bg-[var(--k-color-surface-muted)]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              Tour für diesen Bereich starten
            </button>
          ) : null}

          {hasContextWorkflows ? (
            <div className="space-y-1">
              <p className="px-3 pt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--k-color-text-faint)]">
                Geführte Workflows
              </p>
              {contextWorkflows.map((wf) => (
                <button
                  key={wf.code}
                  type="button"
                  onClick={() => {
                    onStartWorkflow(wf.code);
                    setOpen(false);
                  }}
                  className="flex w-full items-start gap-2 rounded-[var(--k-radius-sm)] px-3 py-2 text-left text-sm font-medium text-[var(--k-color-text-secondary)] transition hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className="mt-0.5 shrink-0 text-[var(--k-color-text-link)]"
                  >
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  <span className="flex-1">
                    <span className="block font-semibold text-[var(--k-color-text-primary)]">
                      {wf.title}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-[var(--k-color-text-muted)]">
                      {wf.description}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          <div className="my-1 border-t border-[var(--k-color-border-subtle)]" />
          <button
            type="button"
            onClick={() => {
              onShowWelcomeAgain();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-[var(--k-radius-sm)] px-3 py-2 text-sm font-medium text-[var(--k-color-text-secondary)] transition hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12a9 9 0 1 0 9-9" />
              <polyline points="3 4 3 9 8 9" />
            </svg>
            Willkommen-Modal nochmal zeigen
          </button>
          <button
            type="button"
            onClick={() => {
              onShowWelcome();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-[var(--k-radius-sm)] px-3 py-2 text-sm font-medium text-[var(--k-color-text-secondary)] transition hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12h18M3 6h18M3 18h12" />
            </svg>
            Alle Bereiche ansehen
          </button>
          <button
            type="button"
            onClick={() => {
              onResetTour();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-[var(--k-radius-sm)] px-3 py-2 text-xs font-medium text-[var(--k-color-text-muted)] transition hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
            Touren zurücksetzen
          </button>
          <div className="my-1 border-t border-[var(--k-color-border-subtle)]" />
          <p className="px-3 py-1.5 text-[10px] text-[var(--k-color-text-muted)]">
            Tipp: <kbd className="rounded bg-[var(--k-color-bg-canvas)] px-1 py-0.5 font-mono text-[10px]">Shift + ?</kbd>{' '}
            öffnet diesen Dialog.
          </p>
        </div>
      </div>
    </details>
  );
}
